import { createContext, ReactNode, useContext, useMemo } from "react";
import {
  useLoaderData,
  useLocation,
  useNavigate,
  useNavigation,
} from "react-router-dom";
import { PortfolioData } from "../App.tsx";
import { AgreementType, isAgreementType } from "../shared/AgreementType.ts";
import { isSettlementType, SettlementType } from "../shared/SettlementType.ts";

// describes filters for portfolio cases
type PortfolioFilter = {
  // filter cases by types of claims, filters are alternatives
  claimTypes: {
    type: string;
    // number of cases with claims of this type
    casesCount: number;
    // if true, cases with claims of this type are shown
    selected: boolean;
  }[];
  // call to change claim type filters
  selectClaimTypes: (types?: string[]) => void;

  // agreement type filters
  agreementTypes: {
    type: AgreementType;
    casesCount: number;
    selected: boolean;
  }[];
  selectAgreementTypes: (types?: AgreementType[]) => void;
  // settlement type filters
  settlementTypes: {
    type: SettlementType;
    casesCount: number;
    selected: boolean;
  }[];
  selectSettlementTypes: (types?: SettlementType[]) => void;
  // indicates if cases are being queried based on filters. use to indicate progress in UI
  queryOngoing: boolean;
};

// agreement types currently supported in the UI
const supportedAgreementTypes: AgreementType[] = [
  "none",
  "barristerCfa",
  "lawFirmCfa",
];

// settlement types currently supported in the UI
const supportedSettlementTypes: SettlementType[] = ["none", "settled"];

export const PortfolioFilterContext = createContext<
  PortfolioFilter | undefined
>(undefined);

export const usePortfolioFilters = () => {
  const context = useContext(PortfolioFilterContext);
  if (!context) {
    throw new Error(
      "usePortfolioFilters must be used within a PortfolioFilterProvider"
    );
  }
  return context;
};

export const PortfolioFilterProvider = ({
  children,
}: {
  children: ReactNode | ReactNode[];
}) => {
  // get current website location
  const { search, pathname, hash } = useLocation();
  // needed to navigate to new location based on filter params
  const navigate = useNavigate();
  // monitor router state as filters are updated by setting URL parameters
  const { state } = useNavigation();

  const {
    caseCounts: {
      forFilters: { byClaimType, byAgreementType, bySettlement },
    },
  } = useLoaderData() as PortfolioData;

  // memoize available claim types and their states
  const claimTypes: PortfolioFilter["claimTypes"] = useMemo(() => {
    const params = new URLSearchParams(search);
    const selectedClaimTypes = params.getAll("claimType");
    return (
      Object.entries(byClaimType)
        .map(([type, casesCount]) => ({
          type,
          casesCount,
          selected: selectedClaimTypes.includes(type),
        }))
        // UI can sort the claim types e.g. by their labels, but we can put types with most cases forward
        .sort((type1, type2) => {
          const countDifference = type2.casesCount - type1.casesCount;
          return countDifference !== 0
            ? countDifference
            : type1.type.localeCompare(type2.type);
        })
    );
  }, [byClaimType, search]);

  const selectClaimTypes = async (types?: string[]) => {
    const params = new URLSearchParams(search);
    params.delete("claimType");

    types?.forEach((type) => params.append("claimType", type));
    navigate({ pathname, search: params.toString(), hash }, { replace: true });
  };

  const agreementTypes: PortfolioFilter["agreementTypes"] = useMemo(() => {
    const params = new URLSearchParams(search);
    const selectedAgreementTypes: AgreementType[] = params
      .getAll("agreementType")
      .filter(isAgreementType);
    return (
      supportedAgreementTypes
        .map((type) => ({
          type,
          casesCount: byAgreementType[type],
          selected: selectedAgreementTypes.includes(type),
        }))
        // UI can sort the claim types e.g. by their labels, but we can put types with most cases forward
        .sort((type1, type2) => {
          const countDifference = type2.casesCount - type1.casesCount;
          return countDifference !== 0
            ? countDifference
            : type1.type.localeCompare(type2.type);
        })
    );
  }, [byAgreementType, search]);

  const selectAgreementTypes = (types?: AgreementType[]) => {
    const params = new URLSearchParams(search);
    params.delete("agreementType");
    types?.forEach((key) => params.append("agreementType", key));
    navigate({ pathname, search: params.toString(), hash }, { replace: true });
  };

  const settlementTypes: PortfolioFilter["settlementTypes"] = useMemo(() => {
    const params = new URLSearchParams(search);
    const selectedSettlementTypes: SettlementType[] = params
      .getAll("settlement")
      .filter(isSettlementType);

    return supportedSettlementTypes
      .map((type) => ({
        type,
        casesCount: bySettlement[type],
        selected: selectedSettlementTypes.includes(type),
      }))
      .sort((type1, type2) => {
        const countDifference = type2.casesCount - type1.casesCount;
        return countDifference !== 0
          ? countDifference
          : type1.type.localeCompare(type2.type);
      });
  }, [bySettlement, search]);

  const selectSettlementTypes = (types?: SettlementType[]) => {
    const params = new URLSearchParams(search);
    params.delete("settlement");
    types?.forEach((key) => params.append("settlement", key));
    navigate({ pathname, search: params.toString(), hash }, { replace: true });
  };

  const value: PortfolioFilter = {
    claimTypes,
    selectClaimTypes,
    agreementTypes,
    selectAgreementTypes,
    settlementTypes,
    selectSettlementTypes,
    // if router is not idle, it means it is loading new data
    queryOngoing: state !== "idle",
  };

  return (
    <PortfolioFilterContext.Provider value={value}>
      {children}
    </PortfolioFilterContext.Provider>
  );
};
